/**
 * 样式常量 - 卡牌类型样式 & 风险等级徽章
 * 
 * 颜色由卡牌类型(话题/事件/人设)决定，风险等级(safe/normal/risky)只影响小徽章
 */

import type { RiskLevel } from '../types';
import { CardType } from '../types';

// ============================================
// 风险等级样式（仅用于小徽章/指示器）
// ============================================
export const RISK_STYLES: Record<RiskLevel, {
  border: string;
  bg: string;
  text: string;
  badge: string;
  label: string;
  /** 风险等级图标符号 */
  icon: string;
}> = {
  safe: {
    border: 'border-green-400',
    bg: 'bg-green-50',
    text: 'text-green-600',
    badge: 'bg-green-500',
    label: '安全',
    icon: '✦',
  },
  normal: {
    border: 'border-amber-400',
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    badge: 'bg-amber-500',
    label: '中等',
    icon: '✦✦',
  },
  risky: {
    border: 'border-red-400',
    bg: 'bg-red-50',
    text: 'text-red-600',
    badge: 'bg-red-500',
    label: '风险',
    icon: '✦✦✦',
  },
};

// ============================================
// 卡牌类型样式配置（主视觉由类型决定）
// 话题 = 蓝/青色 💬  事件 = 橙/琥珀色 ⚡  人设 = 紫色 ✨
// ============================================
export const CARD_TYPE_STYLE_CONFIG: Record<CardType, {
  bgGradient: string;
  border: string;
  iconBg: string;
  iconColor: string;
  textColor: string;
  accentColor: string;
  glowClass: string;
  shadow: string;
}> = {
  [CardType.TOPIC]: {
    bgGradient: 'bg-gradient-to-br from-sky-50 via-cyan-50 to-sky-100',
    border: 'border-sky-400',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    textColor: 'text-sky-800',
    accentColor: 'text-sky-400',
    glowClass: 'shadow-[0_0_12px_rgba(56,189,248,0.25)]',
    shadow: 'shadow-lg shadow-sky-100/50',
  },
  [CardType.EVENT]: {
    bgGradient: 'bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100',
    border: 'border-amber-400',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    textColor: 'text-amber-900',
    accentColor: 'text-amber-500',
    glowClass: '',
    shadow: 'shadow-lg shadow-amber-100/50',
  },
  [CardType.PERSONA]: {
    bgGradient: 'bg-gradient-to-br from-violet-50 via-purple-50 to-violet-100',
    border: 'border-violet-400',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    textColor: 'text-violet-900',
    accentColor: 'text-violet-400',
    glowClass: 'shadow-[0_0_15px_rgba(167,139,250,0.3)]',
    shadow: 'shadow-lg shadow-violet-100/50',
  },
};


// ============================================
// CP 进度条样式
// ============================================
export const CP_STYLES = {
  high: {
    gradient: 'linear-gradient(90deg, #FFB347 0%, #FF6B6B 100%)',
    heartColor: 'text-[#FF6B6B] fill-[#FF6B6B]',
  },
  low: {
    gradient: 'linear-gradient(90deg, #6BB8FF 0%, #93C5FD 100%)',
    heartColor: 'text-[#6BB8FF]',
  },
} as const;

// ============================================
// 主题颜色
// ============================================
export const THEME_COLORS = {
  primary: '#FF6B6B',
  secondary: '#9B8EC4',
  accent: '#FFB347',
  background: '#FFF8F0',
  border: '#E5E0D8',
  text: {
    primary: '#2D2D2D',
    secondary: '#6B7280',
    muted: '#9CA3AF',
  },
} as const;

// ============================================
// 卡牌类型图标映射
// ============================================
import type { ElementType } from 'react';
import {
  MessageCircle,
  Zap,
  Sparkles
} from 'lucide-react';

export const CARD_TYPE_ICON: Record<string, ElementType> = {
  '话题': MessageCircle,
  '事件': Zap,
  '人设': Sparkles,
};
